import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchSeasonalEvents,
  type SeasonalEvent,
} from "~/integrations/firebase/collections/seasonal-events";
import { useSession } from "~/contexts/auth-context";
import { useFeatureFlags } from "~/contexts/feature-flag-context";
import { filterEventsForMarketplace } from "~/components/seasonal-events/marketplace-region";
import { useSeasonalToday } from "~/components/seasonal-events/hooks/use-seasonal-today";

/** The flag hides the UI, not the documents: rules allow any logged-in read. */
export const useSeasonalEventsQuery = (): {
  events: SeasonalEvent[];
} => {
  const { features } = useFeatureFlags();
  const { organisation } = useSession();
  const marketplace = organisation.settings?.marketplace;
  const today = useSeasonalToday();

  const enabled = Boolean(features.guidedSemanticSearch);
  const { data } = useQuery({
    queryKey: ["seasonal-events"],
    queryFn: fetchSeasonalEvents,
    enabled,
    staleTime: 60 * 60 * 1000,
  });

  // Consumers memoize on the array identity, so filtering must not run per render.
  const events = useMemo(
    () =>
      filterEventsForMarketplace(
        (enabled ? data : undefined) ?? [],
        marketplace,
        today,
      ),
    [data, enabled, marketplace, today],
  );

  return { events };
};
